import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button, Notice } from '../components/ui'
import { signedPoints, points, deltaClass } from '../lib/format'

const VERDICTS = {
  win_win: {
    label: 'Both teams gain',
    className: 'border-emerald-700 bg-emerald-950/40 text-emerald-300',
    blurb: 'Their lineup improves too, so there is a real reason to say yes.',
  },
  fair_value: {
    label: 'Fair on paper',
    className: 'border-slate-600 bg-slate-800 text-slate-300',
    blurb: 'Reads as fair against the draft board, but their lineup does not gain.',
  },
}

// A tier means nothing without the position attached: a tier-1 tight end and a
// tier-1 running back are not the same asset. At a streamed position it is
// suppressed outright rather than shown small, because the number is real
// arithmetic about players who are interchangeable, and printing "K1" is an
// invitation to trade for a kicker.
function Tier({ player }) {
  if (player.streamable) {
    return (
      <span
        className="rounded border border-slate-700 px-1 py-0.5 text-[10px] uppercase tracking-wide text-slate-500"
        title="The startable players at this position are within noise of each other — play the matchup, do not trade for one"
      >
        {player.position} streamed
      </span>
    )
  }

  if (!player.tier) {
    return <span className="text-[10px] uppercase tracking-wide text-slate-500">{player.position}</span>
  }

  const waiver = player.tier === 'waiver'

  return (
    <span
      className={`rounded border px-1 py-0.5 text-[10px] uppercase tracking-wide ${
        waiver
          ? 'border-slate-700 text-slate-500'
          : 'border-slate-600 text-slate-300'
      }`}
      title={
        waiver
          ? 'Worse than the best player available on waivers'
          : `${signedPoints(player.above_average)} points a week against the average ${player.position}`
      }
    >
      {player.position} {waiver ? 'waiver' : `tier ${player.tier}`}
    </span>
  )
}

function PlayerLine({ player }) {
  const gap = player.gap

  return (
    <li className="flex items-center justify-between gap-2 py-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-sm text-slate-100">{player.name}</span>
        <Tier player={player} />
      </span>
      <span className="shrink-0 text-right text-xs tabular-nums text-slate-400">
        {points(player.projected_points)}
        {/* The market's opinion minus what he actually does. Only worth the
            pixels when the two disagree enough to act on. */}
        {gap >= 1 && <span className="ml-1.5 text-emerald-400">underrated</span>}
        {gap <= -1 && <span className="ml-1.5 text-amber-400">overpriced</span>}
      </span>
    </li>
  )
}

function Suggestion({ suggestion, leagueId, myTeamId }) {
  const navigate = useNavigate()
  const verdict = VERDICTS[suggestion.verdict] || VERDICTS.fair_value
  const estimated = suggestion.estimated

  // Built here rather than server-side so a suggestion stays a suggestion until
  // someone acts on it. This is the ordinary create-a-trade path, which is what
  // makes the evaluation on the other side metered like any other.
  const open = useMutation({
    mutationFn: () => {
      const assets = [
        ...suggestion.receiving.map((player) => ({
          player_id: player.id,
          from_team_id: suggestion.counterparty.id,
          to_team_id: myTeamId,
        })),
        ...suggestion.giving.map((player) => ({
          player_id: player.id,
          from_team_id: myTeamId,
          to_team_id: suggestion.counterparty.id,
        })),
      ]

      return api.createTrade(leagueId, assets)
    },
    onSuccess: (data) => navigate(`/trades/${data.trade.id}`),
  })

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${verdict.className}`}
          >
            {verdict.label}
          </span>
          <span className="text-xs text-slate-400">
            with <span className="text-slate-200">{suggestion.counterparty.name}</span>
          </span>
        </span>

        <span className={`text-sm font-semibold tabular-nums ${deltaClass(estimated.points_per_week)}`}>
          {signedPoints(estimated.points_per_week)}
          <span className="ml-1 text-[10px] font-normal uppercase text-slate-500">pts/wk est.</span>
        </span>
      </div>

      <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">You get</div>
          <ul className="mt-0.5">
            {suggestion.receiving.map((player) => (
              <PlayerLine key={player.id} player={player} />
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">You give</div>
          <ul className="mt-0.5">
            {suggestion.giving.map((player) => (
              <PlayerLine key={player.id} player={player} />
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-2 text-xs text-slate-400">
        {verdict.blurb}
        {suggestion.headline_risk && (
          <span className="text-amber-400">
            {' '}They give up the biggest name in the deal, which some managers refuse on principle.
          </span>
        )}
      </p>

      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] tabular-nums text-slate-500">
          Their lineup {signedPoints(estimated.their_points_per_week)} · draft board{' '}
          {signedPoints(estimated.their_market_delta)}
        </span>

        <Button variant="secondary" onClick={() => open.mutate()} disabled={open.isPending}>
          {open.isPending ? 'Opening…' : 'Evaluate this trade'}
        </Button>
      </div>

      <ErrorMessage error={open.error} />
    </div>
  )
}

export default function Suggestions() {
  const { leagueId } = useParams()
  const [teamId, setTeamId] = useState(null)

  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => api.league(leagueId),
  })

  const teams = league.data?.teams || []
  // Falls through to the first team rather than nothing, because the server
  // does the same when a user has no team of their own in this league — and a
  // select showing blank while the list below answers for somebody is worse
  // than either on its own.
  const chosenTeam = teamId || league.data?.league?.my_team_id || teams[0]?.id || null

  const suggestions = useQuery({
    queryKey: ['leagueSuggestions', leagueId, chosenTeam],
    queryFn: () => api.leagueSuggestions(leagueId, { teamId: chosenTeam }),
    enabled: !!league.data,
    // The search is seconds of work, and the server caches it for half an hour
    // anyway. Refetching it because a tab regained focus would spend that for
    // an answer that cannot have changed.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const result = suggestions.data
  const rows = result?.suggestions || []
  const streamed = Object.entries(result?.positions || {}).filter(([, line]) => line.streamable)

  return (
    <Layout>
      <div className="space-y-4">
        <div>
          <Link to={`/leagues/${leagueId}`} className="text-xs text-slate-400 hover:text-slate-200">
            ← {league.data?.league?.name || 'League'}
          </Link>
          <h1 className="mt-1.5 text-lg font-semibold text-slate-100">Trade suggestions</h1>
          <p className="text-xs text-slate-400">
            Ranked by what they do for your lineup, filtered to deals the other manager has a
            reason to accept. Numbers here are estimates — open one for the full evaluation.
          </p>
        </div>

        {teams.length > 0 && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">Suggesting for</span>
            <select
              value={chosenTeam || ''}
              onChange={(event) => setTeamId(event.target.value)}
              className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.id === league.data?.league?.my_team_id ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {(league.isPending || suggestions.isPending) && (
          <Card>
            <Spinner label="Pricing every roster in the league…" />
            <p className="mt-1.5 text-xs text-slate-500">
              Every player is valued against every team's lineup, then the packages worth
              proposing are ranked. This takes a few seconds the first time.
            </p>
          </Card>
        )}

        <ErrorMessage error={league.error || suggestions.error} />

        {result && rows.length === 0 && (
          <Notice title="Nothing worth proposing right now">
            {result.meta?.note ||
              'No package clears the bar: every deal that would help your lineup would leave the other manager visibly worse off.'}
          </Notice>
        )}

        {rows.map((suggestion, index) => (
          <Suggestion
            key={`${suggestion.counterparty.id}-${index}`}
            suggestion={suggestion}
            leagueId={leagueId}
            myTeamId={result.team.id}
          />
        ))}

        {streamed.length > 0 && (
          <Card title="Positions where the player is not the asset">
            <p className="text-xs text-slate-400">
              At{' '}
              <span className="text-slate-200">
                {streamed.map(([position]) => position).join(', ')}
              </span>{' '}
              the startable players in this league are closer together than their own
              projections move week to week. Fill those slots off the wire and play the
              matchup — suggestions never offer to trade for one.
            </p>
          </Card>
        )}
      </div>
    </Layout>
  )
}
